/**
 * Le client Supabase, chargé À LA DEMANDE.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LE PROBLÈME
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `./supabase` appelle `createClient` au niveau module. Tout fichier qui
 * l'importe STATIQUEMENT fait donc entrer le SDK entier dans son chunk —
 * auth-js, realtime-js et son moteur phoenix, storage-js, postgrest-js.
 *
 * Trois chemins PUBLICS le faisaient entrer dans le chunk d'entrée :
 *
 *   · App.tsx           -> AuthProvider, importé statiquement alors qu'il
 *                          n'enveloppe que les routes /admin ;
 *   · useSitePageContent -> monté par l'accueil et cinq autres pages ;
 *   · lib/devis.ts      -> le formulaire de devis, présent dans le hero.
 *
 * Résultat mesuré sur l'accueil (audit du 07/09/2026) : `vendor-supabase`
 * en `modulepreload`, 47,8 Kio transférés dont 38,2 Kio jamais exécutés. Sur
 * un site dont les 122 pages sont PRÉ-RENDUES, aucune de ces trois choses
 * n'est nécessaire au premier rendu :
 *
 *   · l'admin n'est jamais montée sur une page publique ;
 *   · les surcharges CMS arrivent dans un `useEffect`, donc après la peinture ;
 *   · une demande de devis part sur un clic, jamais au chargement.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CE MODULE, ET POURQUOI PAS UNE RÉÉCRITURE DE `./supabase`
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Seize fichiers du back-office font `import { supabase } from './supabase'`
 * et s'en servent de façon synchrone. Changer la forme de ce module-là aurait
 * touché seize fichiers de l'admin pour un gain qui ne concerne que le site
 * public — beaucoup de risque, aucun bénéfice de plus.
 *
 * `./supabase` est donc laissé STRICTEMENT INTACT. Ce module ajoute, à côté,
 * la seule chose qui manquait : une porte asynchrone. Les modules de l'admin
 * étant tous chargés en `lazy()`, plus aucun import statique ne subsiste
 * depuis l'entrée, et Rollup relègue le SDK dans un chunk asynchrone.
 *
 * ⚠️ `import type` est effacé à la compilation : il ne crée aucune dépendance
 * à l'exécution. C'est ce qui permet de garder le typage complet sans ramener
 * une seule ligne du SDK dans le bundle.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Mémorisée : le SDK n'est téléchargé, analysé et instancié qu'une fois, même
 * si deux formulaires de la même page partent coup sur coup.
 */
let promesse: Promise<SupabaseClient> | null = null;

/** Charge le SDK si besoin, puis rend le client partagé de `./supabase`. */
export function chargerSupabase(): Promise<SupabaseClient> {
  promesse ??= import('./supabase').then((m) => m.supabase);
  return promesse;
}
